import { ANALYTICS_EVENTS } from '../constants/analytics';
import { safeAnalytics } from './analytics';

/**
 * Where the app origin's CSP violations land (#4771). `firebase.json` sends
 * the policy with no `report-uri`, so a violation would otherwise reach only
 * the console of the browser it happened in. Each one becomes a
 * `CSP Violation Reported` event, which is what the flip from Report-Only to
 * enforcing is decided on (`.agents/rules/security.md`).
 *
 * Most violations fire while the page loads, before `main.tsx`'s deferred
 * `safeAnalytics.init()`, so they are stashed and flushed after init and on
 * every consent grant (`flushPendingAnalyticsEvents`), the same model as
 * `recordingRecoveryTelemetry.ts`. Only origins are kept, never a path or
 * query, and violations from browser extensions are dropped as noise.
 */

const PENDING_KEY = 'bower:pending_csp_violations';
const MAX_PENDING = 20;

export type CspViolationProps = {
  directive: string;
  blocked: string;
  source: string;
  disposition: string;
};

const EXTENSION_SCHEME = /^(chrome|moz|safari-web|ms-browser)-extension:/;

/** `blockedURI` is a keyword (`inline`, `eval`, `data`) or a URL; keep only a URL's origin. */
function originOf(value: string): string {
  if (!value || !value.includes(':')) return value || 'none';
  try {
    const { origin, protocol } = new URL(value);
    return origin === 'null' ? protocol.replace(/:$/, '') : origin;
  } catch {
    return 'unparseable';
  }
}

/** sessionStorage is client-writable and becomes event properties: input, not payload. */
function isViolationProps(value: unknown): value is CspViolationProps {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.directive === 'string' &&
    typeof v.blocked === 'string' &&
    typeof v.source === 'string' &&
    typeof v.disposition === 'string' &&
    Object.keys(v).length === 4
  );
}

function readStash(): CspViolationProps[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(PENDING_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isViolationProps) : [];
  } catch {
    return [];
  }
}

function writeStash(entries: CspViolationProps[]): void {
  try {
    if (entries.length === 0) sessionStorage.removeItem(PENDING_KEY);
    else sessionStorage.setItem(PENDING_KEY, JSON.stringify(entries));
  } catch {
    // Storage gone: so is the stash.
  }
}

// One event per distinct violation per page load: a blocked asset in a loop
// files the same report on every render.
const seen = new Set<string>();

export function reportCspViolation(event: SecurityPolicyViolationEvent): void {
  if (EXTENSION_SCHEME.test(event.sourceFile) || EXTENSION_SCHEME.test(event.blockedURI)) return;
  const props: CspViolationProps = {
    directive: event.effectiveDirective,
    blocked: originOf(event.blockedURI),
    source: originOf(event.sourceFile),
    disposition: event.disposition,
  };
  const key = `${props.directive} ${props.blocked} ${props.source}`;
  if (seen.has(key)) return;
  seen.add(key);
  if (safeAnalytics.isReady()) {
    safeAnalytics.track(ANALYTICS_EVENTS.CSP_VIOLATION_REPORTED, props);
    return;
  }
  writeStash([...readStash(), props].slice(-MAX_PENDING));
}

/** Deliver stashed violations. Checks readiness BEFORE clearing, as the model does. */
export function flushPendingCspViolations(): void {
  const pending = readStash();
  if (pending.length === 0 || !safeAnalytics.isReady()) return;
  for (const props of pending) {
    safeAnalytics.track(ANALYTICS_EVENTS.CSP_VIOLATION_REPORTED, props);
  }
  writeStash([]);
}

/** Called once from `main.tsx`, as early as its imports allow. */
export function listenForCspViolations(): void {
  document.addEventListener('securitypolicyviolation', reportCspViolation);
}
